import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { trackRecurrency } from '@cannect/services/events'
import {
  QUERY_KEYS,
  SUBSCRIPTIONS_PAGE_PATH,
  SUBSCRIPTIONS_PAGE_READY
} from '@cannect/services/resources/recurrency/constants'
import { formatApiDate } from '@cannect/services/resources/recurrency/helpers'
import { useRecurrencyAccess, useSubscriptions } from '@cannect/services/resources/recurrency/hooks'
import { getSubscription } from '@cannect/services/resources/recurrency/requests'
import type { SubscriptionView } from '@cannect/services/resources/recurrency/types'
import { recurrencyCopy } from '../copy'

const MAX_REFETCHES = 3
const REFETCH_MS = 5000

export const orderHasRecurrencyPlan = (order: any) =>
  Boolean(order?.recurrencySummary || (order?.items || []).some((item: any) => item?.recurrency_plan_id))

// Pedido gerado pelo motor a cada ciclo (apiCannect recurrencyOrderService grava
// `originCheckout: 'recorrencia'`). Ele também traz `recurrency_plan_id` nos
// itens, mas a assinatura já existia: o bloco "Assinatura criada" e o evento de
// adesão são só do pedido da adesão (T079).
const RECURRENCY_CYCLE_ORIGIN = 'recorrencia'
export const isRecurrencyCycleOrder = (order: any) =>
  String(order?.originCheckout ?? order?.origin_checkout ?? '').toLowerCase() === RECURRENCY_CYCLE_ORIGIN

const trackedKey = (orderId: number) => `recurrency_adhesion_tracked_${orderId}`

// 010: o checkout do link do painel marca o pedido ao pagar (research R11).
const fromCartLink = (orderId: number) => {
  try {
    return sessionStorage.getItem(`recurrency_cart_link_${orderId}`) === '1'
  } catch {
    return false
  }
}

const alreadyTracked = (orderId: number) => {
  try {
    return sessionStorage.getItem(trackedKey(orderId)) === '1'
  } catch {
    return false
  }
}

const markTracked = (orderId: number) => {
  try {
    sessionStorage.setItem(trackedKey(orderId), '1')
  } catch {
    // sem storage: a ref do componente ainda evita repetir na mesma tela
  }
}

// "Assinatura criada" no destino pós-pagamento (research R14). A assinatura é
// criada pelo serviço depois do pagamento aprovado; até aparecer, o bloco
// avisa e tenta de novo algumas vezes.
// `context` (009): 'upload' na tela de envio de documentos (o envio fica logo
// abaixo); 'order' no detalhe do pedido (atalho para a tela de envio).
export const RecurrencyAdhesionBlock = ({ order, context = 'order' }: { order: any; context?: 'upload' | 'order' }) => {
  const { enabled } = useRecurrencyAccess()
  const client = useQueryClient()
  const hasPlan = enabled && orderHasRecurrencyPlan(order) && !isRecurrencyCycleOrder(order)
  const orderId = Number(order?.id)
  const [attempts, setAttempts] = useState(0)
  const tracked = useRef(false)
  const { data: subscriptions = [], refetch } = useSubscriptions()
  const candidates = hasPlan ? subscriptions.filter((s) => s.status === 'active' || s.status === 'paused') : []
  // Com mais de uma (dependentes), o detalhe diz qual tem o ciclo deste pedido.
  const details = useQueries({
    queries: (candidates.length > 1 ? candidates : []).map((s) => ({
      queryKey: QUERY_KEYS.subscription(s.id),
      queryFn: () => getSubscription(s.id)
    }))
  })
  const match: SubscriptionView | undefined =
    candidates.length === 1
      ? candidates[0]
      : details.find((d) => d.data?.cycles?.some((c) => Number(c.orderId) === orderId))?.data

  useEffect(() => {
    if (!hasPlan || match || attempts >= MAX_REFETCHES) return undefined
    const timer = setTimeout(() => {
      setAttempts((n) => n + 1)
      refetch()
    }, REFETCH_MS)
    return () => clearTimeout(timer)
  }, [hasPlan, match, attempts])

  useEffect(() => {
    if (!match || tracked.current) return
    tracked.current = true
    client.invalidateQueries({ queryKey: QUERY_KEYS.access })
    if (alreadyTracked(orderId)) return
    markTracked(orderId)
    trackRecurrency('recurrency_adhesion', {
      origin: 'checkout',
      cycleDays: match.cycleDays,
      items: match.items.map((item) => ({ productId: item.productId, quantity: item.quantity })),
      documentsPending: Boolean(match.documentsPending),
      ...(fromCartLink(orderId) ? { source: 'cart_link' as const } : {})
    })
  }, [match])

  if (!hasPlan) return null

  const documentsPending = match?.documentsPending === true
  const linkClass = 'font-semibold text-primary-700 underline'
  const createdText = () => {
    if (!match) return ''
    if (!documentsPending)
      return recurrencyCopy.adhesion.created(formatApiDate(match.nextChargeAt, 'dd/MM'), match.cycleDays)
    return context === 'upload'
      ? recurrencyCopy.adhesion.createdPendingUpload
      : recurrencyCopy.adhesion.createdPendingOrder
  }

  return (
    <div
      role="status"
      className="w-full rounded-xl border border-solid border-primary-200 bg-primary-50 p-4 text-sm text-gray-800">
      {match ? (
        <p>
          <strong className="font-semibold">{createdText()}</strong>
          {documentsPending && context === 'order' && (
            <>
              {' '}
              <Link to={`/enviar-receita/${orderId}`} className={linkClass}>
                {recurrencyCopy.adhesion.sendDocuments}
              </Link>
            </>
          )}
          {SUBSCRIPTIONS_PAGE_READY && (
            <>
              {' '}
              <Link to={SUBSCRIPTIONS_PAGE_PATH} className={linkClass}>
                {recurrencyCopy.adhesion.link}
              </Link>
            </>
          )}
        </p>
      ) : (
        <p>{SUBSCRIPTIONS_PAGE_READY ? recurrencyCopy.adhesion.pending : recurrencyCopy.adhesion.pendingWithoutPage}</p>
      )}
    </div>
  )
}
